import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { employerAPI } from '../../services/api';
import { toast } from 'sonner';
import { TrendingUp, Award, Users, BarChart3 } from 'lucide-react';

const EmployerAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await employerAPI.getAnalytics();
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B5AFF]"></div>
        </div>
      </DashboardLayout>
    );
  }

  const maxRating = 5;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0F1E]">Team Analytics</h1>
          <p className="text-[#5B6472]">Track your team's performance over time</p>
        </div>

        {/* Completion Rate Card */}
        <Card className="bg-gradient-to-r from-[#1B5AFF]/20 to-[#FFFFFF] border border-[#1B5AFF]/30 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#5B6472] text-sm mb-1">Review Completion Rate</p>
              <div className="flex items-baseline gap-2">
                <p className="text-4xl font-bold text-[#0A0F1E]">
                  {analytics?.completion_rate?.percentage || 0}%
                </p>
                <p className="text-[#5B6472] text-sm">
                  ({analytics?.completion_rate?.with_reviews || 0} of {analytics?.completion_rate?.total_employees || 0} employees)
                </p>
              </div>
            </div>
            <div className="w-16 h-16 rounded-full bg-[#1B5AFF]/20 flex items-center justify-center">
              <Users className="w-8 h-8 text-[#1B5AFF]" />
            </div>
          </div>
          {analytics?.completion_rate?.total_employees > 0 && analytics?.completion_rate?.percentage < 100 && (
            <p className="mt-4 text-amber-400 text-sm">
              {analytics.completion_rate.total_employees - analytics.completion_rate.with_reviews} employees still need reviews
            </p>
          )}
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Rating Trend */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-[#1B5AFF]" />
              <h2 className="text-lg font-semibold text-[#0A0F1E]">Rating Trend</h2>
            </div>
            
            {analytics?.rating_trend?.length > 0 ? (
              <div className="space-y-4">
                {/* Simple bar chart */}
                <div className="h-48 flex items-end gap-2">
                  {analytics.rating_trend.slice(-12).map((item, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-[#5B6472]">{item.average_rating}</span>
                      <div 
                        className="w-full bg-[#1B5AFF]/80 rounded-t transition-all"
                        style={{ height: `${(item.average_rating / maxRating) * 100}%` }}
                      />
                      <span className="text-xs text-[#5B6472] rotate-45 origin-left whitespace-nowrap">
                        {item.month.slice(5)}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Legend */}
                <div className="flex justify-between text-sm text-[#5B6472] mt-4 border-t border-[#E8EDF5] pt-4">
                  <span>
                    Avg: {(analytics.rating_trend.reduce((sum, r) => sum + r.average_rating, 0) / analytics.rating_trend.length).toFixed(1)} / {maxRating}
                  </span>
                  <span>
                    Total reviews: {analytics.rating_trend.reduce((sum, r) => sum + r.review_count, 0)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center">
                <p className="text-[#5B6472]">No rating data yet</p>
              </div>
            )}
          </Card>

          {/* Top Skills */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <Award className="w-5 h-5 text-[#1B5AFF]" />
              <h2 className="text-lg font-semibold text-[#0A0F1E]">Most Recognized Skills</h2>
            </div>
            
            {analytics?.top_skills?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_skills.map((skill, index) => {
                  const maxCount = analytics.top_skills[0]?.count || 1;
                  const percentage = (skill.count / maxCount) * 100;
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#0A0F1E]">{skill.skill}</span>
                        <span className="text-[#5B6472]">{skill.count} mentions</span>
                      </div>
                      <div className="h-2 bg-[#F5F8FF] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#1B5AFF] to-[#1B5AFF]/50 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center">
                <p className="text-[#5B6472]">No skills data yet</p>
              </div>
            )}
          </Card>
        </div>

        {/* Empty State */}
        {!analytics?.rating_trend?.length && !analytics?.top_skills?.length && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-12 text-center">
            <BarChart3 className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No Analytics Data Yet</h3>
            <p className="text-[#5B6472] max-w-md mx-auto">
              Start creating performance reviews to see analytics about your team's performance trends and most recognized skills.
            </p>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EmployerAnalytics;
